import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import './AffiliateManagement.css';

const AffiliateManagement = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('PENDING');

  useEffect(() => {
    fetchAffiliates();
  }, [selectedStatus]);

  const fetchAffiliates = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/affiliates-list?status=${selectedStatus}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        // Try to parse error response if possible
        let errorMessage = 'Failed to fetch affiliates';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          console.error('Error response:', errorData);
        } catch (parseError) {
          // If we can't parse JSON, get the text content
          try {
            const textContent = await response.text();
            console.error('Non-JSON response:', textContent);
            errorMessage = `Server returned non-JSON response: ${textContent.substring(0, 100)}...`;
          } catch (textError) {
            console.error('Could not read response text:', textError);
          }
        }
        throw new Error(errorMessage);
      }
      
      // Try to parse the response as text first to debug
      const responseText = await response.text();
      
      // Then parse as JSON
      let data;
      try {
        data = JSON.parse(responseText);
        // Extract the affiliates array from the response
        if (data.success && Array.isArray(data.affiliates)) {
          setAffiliates(data.affiliates);
        } else {
          setAffiliates([]);
          console.warn('Unexpected response format:', data);
        }
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new Error(`Failed to parse response as JSON: ${responseText.substring(0, 100)}...`);
      }
      
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load affiliate applications');
      console.error('Error fetching affiliates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (affiliateId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/affiliates/${affiliateId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to update affiliate status';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          const textContent = await response.text();
          errorMessage = `Server returned non-JSON response: ${textContent.substring(0, 100)}...`;
        }
        throw new Error(errorMessage);
      }
      
      // Show success message
      toast.success(`Affiliate status updated to ${newStatus}`);
      
      // Refresh the list after status update
      fetchAffiliates();
    } catch (err) {
      const errorMessage = err.message || 'Failed to update affiliate status';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error updating affiliate status:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading affiliate applications...</div>;
  }

  if (error) {
    return (
      <div className="error">
        <p>{error}</p>
        <div className="error-actions">
          <button onClick={fetchAffiliates}>Retry</button>
          <button 
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/test`, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                });
                
                const text = await response.text();
                
                try {
                  const json = JSON.parse(text);
                  alert('Test successful: ' + JSON.stringify(json));
                } catch (e) {
                  console.error('Test parse error:', e);
                  alert('Test response not valid JSON: ' + text.substring(0, 100));
                }
              } catch (err) {
                console.error('Test request error:', err);
                alert('Test request failed: ' + err.message);
              }
            }}
          >
            Test API
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="affiliate-management">
      <div className="status-filter">
        <select 
          value={selectedStatus} 
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="PENDING">Pending</option>
          <option value="ACTIVE">Active</option>
          <option value="SUSPENDED">Suspended</option>
        </select>
      </div>

      <div className="affiliates-list">
        {affiliates.length === 0 ? (
          <p className="no-affiliates">No {selectedStatus.toLowerCase()} affiliate applications found.</p>
        ) : (
          affiliates.map(affiliate => (
            <div key={affiliate.id} className="affiliate-card">
              <div className="affiliate-info">
                <h3>{affiliate.businessName}</h3>
                <p className="business-type">{affiliate.businessType}</p>
                <p className="website">{affiliate.website}</p>
                <p className="created-date">Applied: {new Date(affiliate.createdAt).toLocaleDateString()}</p>
              </div>
              
              <div className="affiliate-actions">
                {selectedStatus === 'PENDING' && (
                  <>
                    <button
                      className="approve-button"
                      onClick={() => handleStatusChange(affiliate.id, 'ACTIVE')}
                    >
                      Approve
                    </button>
                    <button
                      className="reject-button"
                      onClick={() => handleStatusChange(affiliate.id, 'SUSPENDED')}
                    >
                      Reject
                    </button>
                  </>
                )}
                {selectedStatus === 'ACTIVE' && (
                  <button
                    className="suspend-button"
                    onClick={() => handleStatusChange(affiliate.id, 'SUSPENDED')}
                  >
                    Suspend
                  </button>
                )}
                {selectedStatus === 'SUSPENDED' && (
                  <button
                    className="reactivate-button"
                    onClick={() => handleStatusChange(affiliate.id, 'ACTIVE')}
                  >
                    Reactivate
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AffiliateManagement; 